import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Grid,
    Link,
    Chip,
    Alert,
    CircularProgress,
    Divider,
    IconButton,
    Checkbox,
    FormControlLabel,
    Button,
    Snackbar
} from '@mui/material';
import {
    MusicNote,
    PlaylistPlay,
    OpenInNew,
    TrendingUp,
    Star,
    LibraryMusic,
    Save
} from '@mui/icons-material';
import { useSonicRecommendations } from '../hooks/useSonicRecommendations';
import { useRecommendationSelections } from '../hooks/useRecommendationSelections';
import type { SonicRecommendation } from '../types';

interface SonicRecommendationsProps {
    artistId?: string | null;
    contactId?: string | null;
}

const formatNumber = (num?: number | null): string => {
    if (!num) return 'N/A';
    if (num >= 1000000) return `${(num/1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num/1000).toFixed(1)}K`;
    return num.toString();
};

const getTierFitColor = (score?: number) => {
    if (!score) return 'default';
    if (score >= 0.9) return 'success';
    if (score >= 0.7) return 'warning';
    return 'default';
};

const getTierFitLabel = (score?: number) => {
    if (!score) return 'Unknown';
    if (score >= 0.9) return 'Perfect Fit';
    if (score >= 0.7) return 'Good Fit';
    if (score >= 0.5) return 'Stretch';
    return 'Poor Fit';
};

const SimilarTrackCard = ({ 
    trackName, 
    artistName, 
    spotifyUrl, 
    similarityScore,
    index,
    selected,
    onSelectionChange
}: {
    trackName?: string;
    artistName?: string;
    spotifyUrl?: string;
    similarityScore?: number;
    index: number;
    selected: boolean;
    onSelectionChange: (selected: boolean) => void;
}) => {
    if (!trackName || !artistName) return null;

    return (
        <Card variant="outlined" sx={{ 
            height: '100%',
            border: selected ? 2 : 1,
            borderColor: selected ? 'primary.main' : 'divider',
            bgcolor: selected ? 'primary.light' : 'background.paper',
            opacity: selected ? 1 : 0.8
        }}>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center">
                        <Checkbox 
                            checked={selected}
                            onChange={(e) => onSelectionChange(e.target.checked)}
                            size="small"
                            sx={{ mr: 1, p: 0.5 }}
                        />
                        <Typography variant="h6" component="div" fontSize="0.9rem">
                            #{index}
                        </Typography>
                    </Box>
                    <Chip 
                        label={`${Math.round((similarityScore || 0) * 100)}% match`}
                        color="primary"
                        size="small"
                    />
                </Box>
                
                <Typography variant="body1" fontWeight="bold" noWrap>
                    {trackName}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                    by {artistName}
                </Typography>
                
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                    <MusicNote color="action" fontSize="small" />
                    {spotifyUrl && (
                        <IconButton 
                            size="small"
                            onClick={() => {
                                // Convert https://open.spotify.com/track/id to spotify:track:id for app
                                const spotifyUri = spotifyUrl.replace('https://open.spotify.com/', 'spotify:').replace(/\//g, ':');
                                window.open(spotifyUri, '_self');
                            }}
                            title="Play in Spotify"
                        >
                            <OpenInNew fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

const PlaylistCard = ({ 
    name, 
    link, 
    genres, 
    tierFit, 
    avgRank, 
    confidence,
    type,
    selected,
    onSelectionChange
}: {
    name?: string;
    link?: string;
    genres?: string;
    tierFit?: number;
    avgRank?: number;
    confidence?: number;
    type: 'editorial' | 'user';
    selected: boolean;
    onSelectionChange: (selected: boolean) => void;
}) => {
    if (!name) return null;

    return (
        <Card variant="outlined" sx={{ 
            height: '100%',
            border: selected ? 2 : 1,
            borderColor: selected ? 'primary.main' : 'divider',
            bgcolor: selected ? 'primary.light' : 'background.paper',
            opacity: selected ? 1 : 0.8
        }}>
            <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                    <Checkbox 
                        checked={selected}
                        onChange={(e) => onSelectionChange(e.target.checked)}
                        size="small"
                        sx={{ mr: 1, p: 0.5 }}
                    />
                    {type === 'editorial' ? (
                        <Star color="warning" fontSize="small" sx={{ mr: 1 }} />
                    ) : (
                        <LibraryMusic color="info" fontSize="small" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {type === 'editorial' ? 'Editorial' : 'User'} Playlist
                    </Typography>
                </Box>
                
                <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                    {name}
                </Typography>
                
                {genres && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {genres}
                    </Typography>
                )}
                
                <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                    {tierFit && (
                        <Chip 
                            label={getTierFitLabel(tierFit)}
                            color={getTierFitColor(tierFit)}
                            size="small"
                        />
                    )}
                    {avgRank && (
                        <Chip 
                            label={`Avg rank: ${formatNumber(avgRank)}`}
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
                
                {link && (
                    <Box display="flex" justifyContent="flex-end">
                        <IconButton 
                            size="small"
                            onClick={() => {
                                // Convert playlist URLs to Spotify app format
                                if (link.includes('spotify.com/playlist/')) {
                                    const spotifyUri = link.replace('https://open.spotify.com/', 'spotify:').replace(/\//g, ':');
                                    window.open(spotifyUri, '_self');
                                } else {
                                    window.open(link, '_blank');
                                }
                            }}
                            title="Open Playlist"
                        >
                            <OpenInNew fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const SonicRecommendationsContent = ({ 
    recommendations, 
    artistId, 
    contactId 
}: { 
    recommendations: SonicRecommendation;
    artistId?: string | null;
    contactId?: string | null; 
}) => {
    const { selections, loading: selectionsLoading, updateSelections, saving } = useRecommendationSelections(artistId, contactId);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const similarTracks = [
        {
            trackName: recommendations.similar_track_1_name,
            artistName: recommendations.similar_track_1_artist_name,
            spotifyUrl: recommendations.similar_track_1_spotify_url,
            similarityScore: recommendations.similar_track_1_similarity_score,
        },
        {
            trackName: recommendations.similar_track_2_name,
            artistName: recommendations.similar_track_2_artist_name,
            spotifyUrl: recommendations.similar_track_2_spotify_url,
            similarityScore: recommendations.similar_track_2_similarity_score,
        },
        {
            trackName: recommendations.similar_track_3_name,
            artistName: recommendations.similar_track_3_artist_name,
            spotifyUrl: recommendations.similar_track_3_spotify_url,
            similarityScore: recommendations.similar_track_3_similarity_score,
        },
    ].filter(track => track.trackName && track.artistName);

    const editorialPlaylists = [
        {
            name: recommendations.editorial_playlist_1_name,
            link: recommendations.editorial_playlist_1_link,
            genres: recommendations.editorial_playlist_1_genres,
            tierFit: recommendations.editorial_playlist_1_tier_fit,
            avgRank: recommendations.editorial_playlist_1_avg_rank,
            confidence: recommendations.editorial_playlist_1_confidence,
        },
        {
            name: recommendations.editorial_playlist_2_name,
            link: recommendations.editorial_playlist_2_link,
            genres: recommendations.editorial_playlist_2_genres,
            tierFit: recommendations.editorial_playlist_2_tier_fit,
            avgRank: recommendations.editorial_playlist_2_avg_rank,
            confidence: recommendations.editorial_playlist_2_confidence,
        },
        {
            name: recommendations.editorial_playlist_3_name,
            link: recommendations.editorial_playlist_3_link,
            genres: recommendations.editorial_playlist_3_genres,
            tierFit: recommendations.editorial_playlist_3_tier_fit,
            avgRank: recommendations.editorial_playlist_3_avg_rank,
            confidence: recommendations.editorial_playlist_3_confidence,
        },
    ].filter(playlist => playlist.name);

    const userPlaylists = [
        {
            name: recommendations.user_playlist_1_name,
            link: recommendations.user_playlist_1_link,
            genres: recommendations.user_playlist_1_genres,
            tierFit: recommendations.user_playlist_1_tier_fit,
            avgRank: recommendations.user_playlist_1_avg_rank,
            confidence: recommendations.user_playlist_1_confidence,
        },
        {
            name: recommendations.user_playlist_2_name,
            link: recommendations.user_playlist_2_link,
            genres: recommendations.user_playlist_2_genres,
            tierFit: recommendations.user_playlist_2_tier_fit,
            avgRank: recommendations.user_playlist_2_avg_rank,
            confidence: recommendations.user_playlist_2_confidence,
        },
        {
            name: recommendations.user_playlist_3_name,
            link: recommendations.user_playlist_3_link,
            genres: recommendations.user_playlist_3_genres,
            tierFit: recommendations.user_playlist_3_tier_fit,
            avgRank: recommendations.user_playlist_3_avg_rank,
            confidence: recommendations.user_playlist_3_confidence,
        },
    ].filter(playlist => playlist.name);

    // Handler for updating track selections
    const handleTrackSelectionChange = (trackIndex: number, selected: boolean) => {
        if (!selections) return;
        
        const updatedTracks = selected 
            ? [...selections.selected_similar_tracks, trackIndex]
            : selections.selected_similar_tracks.filter(i => i !== trackIndex);
        
        updateSelections({
            selected_similar_tracks: updatedTracks
        });
    };

    // Handler for updating editorial playlist selections
    const handleEditorialPlaylistSelectionChange = (playlistIndex: number, selected: boolean) => {
        if (!selections) return;
        
        const updatedPlaylists = selected 
            ? [...selections.selected_editorial_playlists, playlistIndex]
            : selections.selected_editorial_playlists.filter(i => i !== playlistIndex);
        
        updateSelections({
            selected_editorial_playlists: updatedPlaylists
        });
    };

    // Handler for updating user playlist selections
    const handleUserPlaylistSelectionChange = (playlistIndex: number, selected: boolean) => {
        if (!selections) return;
        
        const updatedPlaylists = selected 
            ? [...selections.selected_user_playlists, playlistIndex]
            : selections.selected_user_playlists.filter(i => i !== playlistIndex);
        
        updateSelections({
            selected_user_playlists: updatedPlaylists
        });
    };

    // Show selections if available (only when contactId is provided)
    const showSelections = contactId && selections;

    return (
        <Box>
            {/* Header Stats */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                    Sonic Recommendations
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                    <Chip 
                        icon={<TrendingUp />}
                        label={`${Math.round(recommendations.confidence_score * 100)}% confidence`}
                        color="primary"
                        variant="outlined"
                    />
                    <Chip 
                        label={`Rank #${formatNumber(recommendations.cm_artist_rank)}`}
                        color="secondary"
                        variant="outlined"
                    />
                    {showSelections && (
                        <Typography variant="caption" color="text.secondary">
                            {saving ? 'Saving...' : selectionsLoading ? 'Loading...' : 'Auto-saved'}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Similar Tracks */}
            {similarTracks.length > 0 && (
                <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                        <MusicNote sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Similar Tracks
                        {showSelections && selections.selected_similar_tracks.length > 0 && (
                            <Chip 
                                label={`${selections.selected_similar_tracks.length} selected`}
                                size="small"
                                color="primary"
                                sx={{ ml: 2 }}
                            />
                        )}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {similarTracks.map((track, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <SimilarTrackCard
                                    trackName={track.trackName}
                                    artistName={track.artistName}
                                    spotifyUrl={track.spotifyUrl}
                                    similarityScore={track.similarityScore}
                                    index={index + 1}
                                    selected={showSelections ? selections.selected_similar_tracks.includes(index) : false}
                                    onSelectionChange={(selected) => handleTrackSelectionChange(index, selected)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* Editorial Playlists */}
            {editorialPlaylists.length > 0 && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        <Star sx={{ mr: 1, verticalAlign: 'middle' }} color="warning" />
                        Editorial Playlist Opportunities
                        {showSelections && selections.selected_editorial_playlists.length > 0 && (
                            <Chip 
                                label={`${selections.selected_editorial_playlists.length} selected`}
                                size="small"
                                color="primary"
                                sx={{ ml: 2 }}
                            />
                        )}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {editorialPlaylists.map((playlist, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <PlaylistCard
                                    name={playlist.name}
                                    link={playlist.link}
                                    genres={playlist.genres}
                                    tierFit={playlist.tierFit}
                                    avgRank={playlist.avgRank}
                                    confidence={playlist.confidence}
                                    type="editorial"
                                    selected={showSelections ? selections.selected_editorial_playlists.includes(index) : false}
                                    onSelectionChange={(selected) => handleEditorialPlaylistSelectionChange(index, selected)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* User Playlists */}
            {userPlaylists.length > 0 && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        <PlaylistPlay sx={{ mr: 1, verticalAlign: 'middle' }} color="info" />
                        User Playlist Opportunities
                        {showSelections && selections.selected_user_playlists.length > 0 && (
                            <Chip 
                                label={`${selections.selected_user_playlists.length} selected`}
                                size="small"
                                color="primary"
                                sx={{ ml: 2 }}
                            />
                        )}
                    </Typography>
                    <Grid container spacing={2}>
                        {userPlaylists.map((playlist, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <PlaylistCard
                                    name={playlist.name}
                                    link={playlist.link}
                                    genres={playlist.genres}
                                    tierFit={playlist.tierFit}
                                    avgRank={playlist.avgRank}
                                    confidence={playlist.confidence}
                                    type="user"
                                    selected={showSelections ? selections.selected_user_playlists.includes(index) : false}
                                    onSelectionChange={(selected) => handleUserPlaylistSelectionChange(index, selected)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </Box>
    );
};

export const SonicRecommendations: React.FC<SonicRecommendationsProps> = ({ artistId, contactId }) => {
    console.log('🎸 SonicRecommendations component mounted with:', { artistId, contactId });
    
    const { recommendations, loading, error } = useSonicRecommendations(artistId);

    // Debug logging
    console.log('🎵 SonicRecommendations Debug:', {
        artistId,
        contactId,
        loading,
        error,
        hasRecommendations: !!recommendations,
        recommendations: recommendations ? 'Found data' : 'No data'
    });

    if (!artistId) {
        console.log('🎵 No artistId provided');
        return null;
    }

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="center" p={2}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography>Loading sonic recommendations...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                Error loading recommendations: {error}
            </Alert>
        );
    }

    if (!recommendations) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                    No Sonic Recommendations Available
                </Typography>
                <Typography variant="body2">
                    Recommendations haven't been generated for this artist yet. 
                    Run the sonic analysis engine to get similar tracks and playlist opportunities.
                </Typography>
            </Alert>
        );
    }

    return (
        <Card>
            <CardContent>
                <SonicRecommendationsContent 
                    recommendations={recommendations} 
                    artistId={artistId}
                    contactId={contactId}
                />
            </CardContent>
        </Card>
    );
};